/**
 * The review and the draft run the Generate pages read.
 *
 * A run takes up to twenty minutes and the attorney may close the tab, so the
 * page asks for the stored record again while the run can still move, and
 * stops asking once it has ended.
 */

import { useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  GenerateRunResponse,
  RunState,
  ShownTerms,
} from "@patent-studio/api-types";
import {
  readDraftSurvey,
  readGenerateReview,
  readLatestRun,
  readRunDownload,
  startDownload,
  startRun,
} from "@/services/generate";
import { draftSurveyKey, generateReviewKey, latestRunKey } from "./query-keys";

export const RUN_POLL_MS = 5000;

/** A run that is queued or drafting: the task's one active run, which a second start is refused over. */
export function isRunActive(state: RunState | null | undefined): boolean {
  return state === "queued" || state === "running";
}

/** How many polls a ready run's zip is waited for, five minutes at one poll every five seconds. */
export const BUNDLE_WAIT_POLLS = 60;

/** Whether a ready run still lacks the zip its download is signed from. */
export function awaitsBundle(run: GenerateRunResponse | null | undefined) {
  return (
    run?.state === "ready" &&
    !run.artifacts.some((file) => file.kind === "bundle")
  );
}

/**
 * How soon to ask again: while queued or running, while a ready run waits for
 * its zip and `waited` polls have not used up the wait, and never otherwise.
 */
export function pollEvery(
  run: GenerateRunResponse | null | undefined,
  waited = 0,
): number | false {
  if (isRunActive(run?.state)) return RUN_POLL_MS;
  return awaitsBundle(run) && waited < BUNDLE_WAIT_POLLS ? RUN_POLL_MS : false;
}

/**
 * The latest run, and how many polls in a row have failed since the last one
 * that answered. A failed poll keeps the run last read, so the page stays as
 * it was and only says so once the failures run on.
 */
export function useLatestRun(taskId: string) {
  const failedPollsRef = useRef(0);
  const bundleWaitRef = useRef(0);
  const query = useQuery({
    queryKey: latestRunKey(taskId),
    queryFn: async () => {
      try {
        const run = await readLatestRun(taskId);
        failedPollsRef.current = 0;
        bundleWaitRef.current = awaitsBundle(run)
          ? bundleWaitRef.current + 1
          : 0;
        return run;
      } catch (error) {
        failedPollsRef.current += 1;
        throw error;
      }
    },
    refetchInterval: (query) =>
      pollEvery(query.state.data, bundleWaitRef.current),
    // A run takes minutes, and the attorney may wait in another window: a hidden
    // tab asks too, so a draft that lands there shows as ready on return.
    refetchIntervalInBackground: true,
    // Read again whenever a page opens on the run: the start leaves its 202 answer here as
    // fresh, and a worker that took the run a moment later would not show until the next poll.
    refetchOnMount: "always",
    // The next poll is the retry: one retried in place would count each failed
    // poll twice and say the server is out of reach a poll early.
    retry: false,
  });
  return {
    ...query,
    failedPolls: failedPollsRef.current,
    bundleOverdue: bundleWaitRef.current >= BUNDLE_WAIT_POLLS,
  };
}

/**
 * Where the latest run stands, or null with none, read only while `open`. It
 * keeps no poll of its own: the ready page's poll moves the same cached run.
 */
export function useLatestRunState(taskId: string, open: boolean) {
  return useQuery({
    queryKey: latestRunKey(taskId),
    queryFn: () => readLatestRun(taskId),
    enabled: open,
    select: (run) => run?.state ?? null,
  });
}

/**
 * Where the latest run stands, asked again only while it is active, so a page that offers the
 * start knows when one is under way and when it has ended. A failed read is asked again a few
 * times rather than read as no run at all.
 */
export function useLatestRunStanding(taskId: string) {
  return useQuery({
    queryKey: latestRunKey(taskId),
    queryFn: () => readLatestRun(taskId),
    select: (run) => run?.state ?? null,
    refetchInterval: (query) =>
      isRunActive(query.state.data?.state) ? RUN_POLL_MS : false,
    refetchOnMount: "always",
    retry: 3,
  });
}

/** Sign the run's bundle and hand it to the browser to save. */
export function useRunDownload(taskId: string) {
  return useMutation({
    mutationFn: async (runId: string) => {
      const { download_url } = await readRunDownload(taskId, runId);
      startDownload(download_url);
    },
  });
}

/** The review before a draft is generated: every record the run reads, and the gates the start checks. */
export function useGenerateReview(taskId: string) {
  return useQuery({
    queryKey: generateReviewKey(taskId),
    queryFn: () => readGenerateReview(taskId),
  });
}

/**
 * Start a draft run. Given the open items address the attorney read, it starts
 * over those; given none, as Try again on a failed run is, it reads the review
 * afresh and starts over the open items as they now stand. A queued run is
 * the latest run at once; a refusal reads the review again, since what it
 * refused on has moved.
 */
export function useStartRun(taskId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (shown?: {
      openItemsAddress: string;
      terms: ShownTerms | null;
    }) =>
      startRun(
        taskId,
        shown?.openItemsAddress ??
          (await readGenerateReview(taskId)).open_items_address,
        shown?.terms ?? null,
      ),
    // A read of the latest run already out could land after the queued run is written and
    // put the older answer back, so it is called off first.
    onMutate: () => client.cancelQueries({ queryKey: latestRunKey(taskId) }),
    onSuccess: (run) => {
      client.setQueryData(latestRunKey(taskId), run);
    },
    onError: () => {
      void client.invalidateQueries({ queryKey: generateReviewKey(taskId) });
    },
  });
}

/**
 * The finished draft's survey. The survey opens in another tab, so the read is
 * never held fresh, against the application's 30 s: coming back to this tab
 * asks again, and a survey answered meanwhile shows its thanks. A failed read
 * is not retried in place; the next return to the tab is the retry.
 */
export function useDraftSurvey(taskId: string) {
  return useQuery({
    queryKey: draftSurveyKey(taskId),
    queryFn: () => readDraftSurvey(taskId),
    staleTime: 0,
    refetchOnWindowFocus: true,
    retry: false,
  });
}
